import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api';
import { BillingStatusBanner } from '../billing/BillingStatusBanner';
import { useEntitlement } from '../billing/EntitlementContext';

/**
 * Billing page (plan §8). Shows:
 *   - current tariff + billing state + next charge date
 *   - SKU quota usage X / Y
 *   - 12-month payment history
 *   - CTA: change tariff, cancel/resume, checkout, refund
 *
 * Wires to `/api/me/billing` (proxied by excel_service to admin per
 * Phase 7). If the proxy returns 503 (admin unreachable) or 501
 * (ЮКасса not wired yet), we render a quiet "временно недоступно"
 * message — not an error, because phases 6–11 bring it online.
 */

interface TariffRow {
  code: string;
  name: string;
  base_monthly_rub: number;
  per_sku_base_rub: number;
  per_sku_ai_rub: number;
  per_sku_spp_rub: number;
  ai_addon_rub: number;
  sku_quota: number;
  enabled_modules: string[];
  yearly_multiplier: number;
  currency: string;
}

interface BillingHistoryEntry {
  id: string;
  kind: string;
  status: string;
  amount_rub_minor: number;
  period_start: string | null;
  period_end: string | null;
  sku_count_charged: number | null;
  created_at: string;
  settled_at: string | null;
}

interface BillingSubscription {
  id: string;
  tariff_code: string;
  billing_cycle: string;
  state: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at: string | null;
}

interface BillingSummary {
  shop_id: number;
  tariff_code: string;
  sku_quota: number;
  billing_state: string;
  billing_state_since: string | null;
  next_charge_at: string | null;
  is_demo: boolean;
  enabled_modules: string[];
  subscription: BillingSubscription | null;
  history: BillingHistoryEntry[];
}

async function jsonOr<T>(response: Response, fallback: T | null = null): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

function formatRub(minor: number | null | undefined): string {
  if (minor == null) return '—';
  const rub = minor / 100;
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(rub);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

const BILLING_STATE_LABEL: Record<string, string> = {
  ACTIVE: 'Активен',
  PENDING_FIRST_PAYMENT: 'Ожидает первую оплату',
  PAST_DUE: 'Платёж просрочен',
  READ_ONLY: 'Платные модули отключены',
  DOWNGRADED: 'Понижен до базового',
  FROZEN: 'Заморожен',
  CANCELLED: 'Отменён',
};

export function BillingPage() {
  const { entitlement, refresh: refreshEntitlement } = useEntitlement();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [catalog, setCatalog] = useState<TariffRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<{
    busy: boolean;
    message: string | null;
    kind: 'info' | 'warning' | 'error' | 'success' | null;
  }>({ busy: false, message: null, kind: null });

  const loadSummary = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const response = await apiFetch('/api/me/billing');
      if (response.status === 403) {
        setError('Доступ к странице оплаты разрешён только администраторам.');
        setLoading(false);
        return;
      }
      if (!response.ok) {
        const body = await jsonOr<{ detail?: unknown }>(response);
        const detail = body?.detail;
        if (detail && typeof detail === 'object' && 'error' in detail) {
          setError(String((detail as Record<string, unknown>).error));
        } else {
          setError(`http ${response.status}`);
        }
        setLoading(false);
        return;
      }
      const data = (await response.json()) as BillingSummary;
      setSummary(data);
      setLoading(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'unknown error');
      setLoading(false);
    }
  }, []);

  const loadCatalog = useCallback(async (): Promise<void> => {
    try {
      const response = await apiFetch('/api/tariffs');
      if (!response.ok) return;
      const body = (await response.json()) as TariffRow[];
      if (Array.isArray(body)) setCatalog(body);
    } catch {
      // Catalog is secondary — the page still functions without it.
    }
  }, []);

  useEffect(() => {
    void loadSummary();
    void loadCatalog();
  }, [loadSummary, loadCatalog]);

  const runAction = useCallback(
    async (
      label: string,
      exec: () => Promise<Response>,
      successMessage: string,
    ): Promise<void> => {
      setActionState({ busy: true, message: `${label}…`, kind: 'info' });
      try {
        const response = await exec();
        if (response.status === 501) {
          setActionState({
            busy: false,
            message:
              'Оплата через ЮКассу временно недоступна. Обратитесь в поддержку, чтобы оформить подписку вручную.',
            // `warning` (amber) rather than `info` so an owner who
            // tried to pay sees the action clearly did not complete.
            kind: 'warning',
          });
          return;
        }
        if (response.status === 503) {
          setActionState({
            busy: false,
            message: 'Сервис биллинга временно недоступен, попробуйте позже.',
            kind: 'error',
          });
          return;
        }
        if (!response.ok) {
          const body = await jsonOr<{ detail?: unknown }>(response);
          const detail = body?.detail;
          let msg = `Ошибка ${response.status}`;
          if (detail && typeof detail === 'object') {
            const rec = detail as Record<string, unknown>;
            if (typeof rec.error === 'string') msg = rec.error;
          }
          setActionState({ busy: false, message: msg, kind: 'error' });
          return;
        }
        setActionState({ busy: false, message: successMessage, kind: 'success' });
        await loadSummary();
        await refreshEntitlement();
      } catch (err: unknown) {
        setActionState({
          busy: false,
          message: err instanceof Error ? err.message : 'Ошибка сети',
          kind: 'error',
        });
      }
    },
    [loadSummary, refreshEntitlement],
  );

  const onCancel = useCallback(() => {
    void runAction(
      'Отменяем подписку',
      () =>
        apiFetch('/api/me/billing/cancel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }),
      'Отмена запланирована на конец текущего периода. До этого момента функциональность сохраняется.',
    );
  }, [runAction]);

  const onResume = useCallback(() => {
    void runAction(
      'Возобновляем подписку',
      () =>
        apiFetch('/api/me/billing/resume', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }),
      'Подписка продолжит списываться в обычном режиме.',
    );
  }, [runAction]);

  const onCheckout = useCallback(
    (tariff_code: string, billing_cycle: 'monthly' | 'yearly') => {
      void runAction(
        `Переходим на ${tariff_code}`,
        () =>
          apiFetch('/api/me/billing/checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tariff_code,
              billing_cycle,
              return_url: window.location.href,
            }),
          }),
        'Ожидание подтверждения оплаты.',
      );
    },
    [runAction],
  );

  const quotaText = useMemo(() => {
    if (summary == null) return '—';
    const used = entitlement.skuQuotaUsed;
    // `sku_quota = 2_147_483_647` is the "unlimited" sentinel.
    const limit =
      summary.sku_quota >= 2_000_000_000 ? null : summary.sku_quota;
    if (used == null && limit == null) return 'Безлимитно';
    if (used == null) return limit == null ? 'Безлимитно' : `до ${limit}`;
    if (limit == null) return `${used} (безлимитно)`;
    return `${used} / ${limit}`;
  }, [summary, entitlement.skuQuotaUsed]);

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <h1>Оплата</h1>
        <p>Загружаем данные биллинга…</p>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div style={{ padding: 24 }}>
        <h1>Оплата</h1>
        <p style={{ color: '#b91c1c' }}>
          Не удалось загрузить данные биллинга: {error}
        </p>
      </div>
    );
  }

  const s = summary as BillingSummary;
  const cancelPending = s.subscription?.cancel_at != null;

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <h1 style={{ marginBottom: 16 }}>Оплата</h1>
      <BillingStatusBanner />

      <section
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          background: '#fff',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Тариф" value={s.tariff_code} />
          <Field
            label="Статус"
            value={BILLING_STATE_LABEL[s.billing_state] ?? s.billing_state}
          />
          <Field label="SKU" value={quotaText} />
          <Field
            label="Следующее списание"
            value={formatDate(s.next_charge_at ?? s.subscription?.current_period_end ?? null)}
          />
          <Field
            label="Модули"
            value={
              s.enabled_modules.length === 0
                ? '—'
                : s.enabled_modules.join(', ')
            }
          />
          <Field
            label="Цикл"
            value={s.subscription?.billing_cycle ?? '—'}
          />
        </div>
        {s.is_demo && (
          <div
            style={{
              marginTop: 16,
              padding: 10,
              borderRadius: 8,
              background: '#f1f5f9',
              color: '#0f172a',
              fontSize: 13,
            }}
          >
            Demo-магазин не биллится. Все модули всегда включены.
          </div>
        )}
        {cancelPending && (
          <div
            style={{
              marginTop: 16,
              padding: 10,
              borderRadius: 8,
              background: '#fef3c7',
              color: '#92400e',
              fontSize: 13,
            }}
          >
            Подписка будет отменена {formatDate(s.subscription?.cancel_at ?? null)}. До
            этой даты всё работает.
          </div>
        )}
      </section>

      {!s.is_demo && (
        <section
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: 20,
            marginBottom: 24,
            background: '#fff',
          }}
        >
          <h2 style={{ marginTop: 0 }}>Управление подпиской</h2>
          {actionState.message && (
            <div
              role="status"
              style={{
                padding: 10,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 13,
                background:
                  actionState.kind === 'error'
                    ? '#fee2e2'
                    : actionState.kind === 'warning'
                      ? '#fef3c7'
                      : actionState.kind === 'success'
                        ? '#dcfce7'
                        : '#f1f5f9',
                color:
                  actionState.kind === 'error'
                    ? '#991b1b'
                    : actionState.kind === 'warning'
                      ? '#92400e'
                      : actionState.kind === 'success'
                        ? '#166534'
                        : '#0f172a',
              }}
            >
              {actionState.message}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {cancelPending ? (
              <button
                onClick={onResume}
                disabled={actionState.busy}
                style={btnPrimary}
              >
                Вернуть подписку
              </button>
            ) : (
              <button
                onClick={onCancel}
                disabled={actionState.busy}
                style={btnSecondary}
              >
                Отменить на конец периода
              </button>
            )}
          </div>
        </section>
      )}

      {catalog.length > 0 && !s.is_demo && (
        <section
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: 20,
            marginBottom: 24,
            background: '#fff',
          }}
        >
          <h2 style={{ marginTop: 0 }}>Доступные тарифы</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            {catalog.map((t) => (
              <div
                key={t.code}
                style={{
                  border:
                    t.code === s.tariff_code
                      ? '2px solid #2b6eff'
                      : '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: 16,
                  background: t.code === s.tariff_code ? '#eff6ff' : '#fff',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
                  {t.name}
                </div>
                <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
                  до {t.sku_quota} SKU · {t.enabled_modules.join(', ')}
                </div>
                <div style={{ fontSize: 14, color: '#0f172a', marginBottom: 12 }}>
                  от {formatRub(t.base_monthly_rub * 100)} / мес
                </div>
                {t.code !== s.tariff_code ? (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => onCheckout(t.code, 'monthly')}
                      disabled={actionState.busy}
                      style={btnPrimary}
                    >
                      Перейти — месяц
                    </button>
                    <button
                      onClick={() => onCheckout(t.code, 'yearly')}
                      disabled={actionState.busy}
                      style={btnSecondary}
                    >
                      Год (×{t.yearly_multiplier})
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: '#2b6eff', fontWeight: 600 }}>
                    Текущий тариф
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: 20,
          background: '#fff',
        }}
      >
        <h2 style={{ marginTop: 0 }}>История платежей</h2>
        {s.history.length === 0 ? (
          <p style={{ color: '#64748b', margin: 0 }}>Пока нет платежей.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '8px 4px' }}>Дата</th>
                <th style={{ padding: '8px 4px' }}>Тип</th>
                <th style={{ padding: '8px 4px' }}>Статус</th>
                <th style={{ padding: '8px 4px' }}>SKU</th>
                <th style={{ padding: '8px 4px', textAlign: 'right' }}>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {s.history.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 4px' }}>{formatDate(row.created_at)}</td>
                  <td style={{ padding: '8px 4px' }}>{row.kind}</td>
                  <td style={{ padding: '8px 4px' }}>{row.status}</td>
                  <td style={{ padding: '8px 4px' }}>{row.sku_count_charged ?? '—'}</td>
                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                    {formatRub(row.amount_rub_minor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 15, color: '#0f172a', fontWeight: 500 }}>{value}</div>
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#2b6eff',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#fff',
  color: '#0f172a',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
